import React, { useEffect, useState } from 'react';
import { X, PlayCircle, RefreshCw } from 'lucide-react';
import productPreviewHtml from './productPreviewHtml';

/**
 * DemoModeTour
 * ──────────────────────────────────────────────────────────────────────────────
 * A fullscreen, non-interactive product tour overlay that auto-plays through
 * the four core tabs (Today / Build / Money / Team) with sample data so a
 * new user can see the workflow at a glance — without writing any data into
 * their workspace.
 *
 * It uses an iframe with `srcDoc` so the preview renders entirely from the
 * JS bundle — no static-file request, no `X-Frame-Options` from the host,
 * no CDN/edge proxy in the way. Same loop shown on the login page.
 *
 * Trigger this component by setting `showDemoTour = true` in App.js.
 * Nothing in the user's account is changed by opening this overlay.
 */
const DemoModeTour = ({ onClose }) => {
  const [iframeKey, setIframeKey] = useState(0);

  // Lock body scroll while overlay is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Founders+ guided demo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(4, 9, 16, 0.92)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'demoFadeIn 240ms ease',
      }}
    >
      <style>{`
        @keyframes demoFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes demoSlideUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#e6edf6', minWidth: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.1 }}>
              Demo Mode · sample workspace
            </div>
            <div style={{ fontSize: 11, color: '#8ea0b4', marginTop: 2, lineHeight: 1.2 }}>
              Sample data — nothing here is saved to your account. When you exit, your real workspace is unchanged.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setIframeKey(k => k + 1)}
            style={btnGhost}
            title="Replay tour from the start"
          >
            <RefreshCw size={13} strokeWidth={1.8} />
            <span>Replay</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            style={btnPrimary}
            autoFocus
          >
            <X size={14} strokeWidth={2} />
            <span>Skip demo</span>
          </button>
        </div>
      </div>

      {/* Iframe stage */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: 18,
          animation: 'demoSlideUp 320ms ease 80ms backwards',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            background: '#0a0f14',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,212,191,0.08)',
          }}
        >
          <iframe
            key={iframeKey}
            title="Founders+ guided demo"
            srcDoc={productPreviewHtml}
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              display: 'block',
              background: '#0a0f14',
            }}
            // Read-only tour — sandboxed (no same-origin since srcDoc is about:srcdoc)
            sandbox="allow-scripts"
            loading="eager"
          />
        </div>
      </div>

      {/* Bottom hint */}
      <div
        style={{
          padding: '8px 18px 14px',
          color: '#8ea0b4',
          fontSize: 11,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PlayCircle size={12} strokeWidth={1.8} />
          <span>Auto-cycles through Today, Build, Money, and Team — about 22 seconds per loop.</span>
        </div>
        <div>Press <kbd style={kbdStyle}>Esc</kbd> to exit</div>
      </div>
    </div>
  );
};

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', borderRadius: 8,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#cbd5e1', fontSize: 12, fontWeight: 500,
  cursor: 'pointer', transition: 'all 0.15s ease',
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 14px', borderRadius: 8,
  background: 'rgba(45,212,191,0.14)',
  border: '1px solid rgba(45,212,191,0.45)',
  color: '#7ff5e7', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', transition: 'all 0.15s ease',
};

const kbdStyle = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 10,
  fontFamily: 'inherit',
  color: '#cbd5e1',
};

export default DemoModeTour;
